import React, { useEffect, useRef, useState } from 'react';
import Header from './components/Header';
import MobileTaskbar from './components/MobileTaskbar';
import HeroPrompt from './components/HeroPrompt';
import BomUploader from './components/BomUploader';
import ManufacturingShowcase from './components/ManufacturingShowcase';
import ProjectCard from './components/ProjectCard';
import ComponentList from './components/ComponentList';
import ChatSidebar from './components/ChatSidebar';
import UniversalCart from './components/UniversalCart';
import LoadingSkeleton from './components/LoadingSkeleton';
import StoreShowcase from './components/StoreShowcase';
import FooterBanner from './components/FooterBanner';
import ImageMarquee from './components/ImageMarquee';
import ShootingStars from './components/ShootingStars';
import StarsBackground from './components/StarsBackground';
import { getComponentList, getComponentListFromBom } from './services/ai';
import { MessageCircle, RefreshCw, AlertCircle } from 'lucide-react';
import './App.css';

export default function App() {
  const CART_STORAGE_KEY = 'hardware_genie_universal_cart_v1';

  const getInitialTheme = () => {
    const saved = localStorage.getItem('theme');
    if (saved === 'light' || saved === 'dark') {
      return saved;
    }
    return 'light';
  };

  const getInitialCart = () => {
    try {
      const raw = localStorage.getItem(CART_STORAGE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  };

  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [activePanel, setActivePanel] = useState('none'); // none | chat | cart
  const [universalCart, setUniversalCart] = useState(getInitialCart);
  const [currentPrompt, setCurrentPrompt] = useState('');
  const [theme, setTheme] = useState(getInitialTheme);
  const chatOpenTimerRef = useRef(null);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  useEffect(() => {
    localStorage.setItem(CART_STORAGE_KEY, JSON.stringify(universalCart));
  }, [universalCart]);

  const toggleTheme = () => setTheme((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const buildCartKey = (projectTitle, componentId) => `${projectTitle || 'Untitled Project'}::${componentId}`;

  const getLowestStoreId = (component) => {
    if (!component?.stores?.length) return null;
    const withPrices = component.stores
      .map((store) => {
        const parsed = store.priceValue || Number(String(store.estimatedPrice || '').replace(/[^\d.]/g, ''));
        return { storeId: store.storeId, value: Number.isFinite(parsed) ? parsed : null };
      })
      .filter((x) => Number.isFinite(x.value));

    if (!withPrices.length) return component.stores[0].storeId || null;
    withPrices.sort((a, b) => a.value - b.value);
    return withPrices[0].storeId;
  };

  const cartKeySet = new Set(universalCart.map((item) => item.key));

  const toggleComponentInCart = (project, component) => {
    const projectTitle = project?.title || 'Untitled Project';
    const key = buildCartKey(projectTitle, component.id);
    setUniversalCart((prev) => {
      const exists = prev.some((item) => item.key === key);
      if (exists) {
        return prev.filter((item) => item.key !== key);
      }
      const newItem = {
        key,
        projectTitle,
        component,
        quantity: Math.max(1, Number(component.quantity) || 1),
        selectedStoreId: getLowestStoreId(component),
        addedAt: new Date().toISOString(),
      };
      return [...prev, newItem];
    });
  };

  const addManyToCart = (project, components) => {
    const projectTitle = project?.title || 'Untitled Project';
    setUniversalCart((prev) => {
      const seen = new Set(prev.map((item) => item.key));
      const next = [...prev];
      components.forEach((component) => {
        const key = buildCartKey(projectTitle, component.id);
        if (!seen.has(key)) {
          seen.add(key);
          next.push({
            key,
            projectTitle,
            component,
            quantity: Math.max(1, Number(component.quantity) || 1),
            selectedStoreId: getLowestStoreId(component),
            addedAt: new Date().toISOString(),
          });
        }
      });
      return next;
    });
  };

  const removeManyFromCart = (project, components) => {
    const projectTitle = project?.title || 'Untitled Project';
    const removeKeys = new Set(components.map((component) => buildCartKey(projectTitle, component.id)));
    setUniversalCart((prev) => prev.filter((item) => !removeKeys.has(item.key)));
  };

  const removeCartItem = (key) => {
    setUniversalCart((prev) => prev.filter((item) => item.key !== key));
  };

  const updateCartItemQuantity = (key, nextQuantity) => {
    setUniversalCart((prev) =>
      prev.map((item) =>
        item.key === key
          ? { ...item, quantity: Math.max(1, Math.min(999, Number(nextQuantity) || 1)) }
          : item
      )
    );
  };

  const setCartItemStore = (project, component, storeId) => {
    const projectTitle = project?.title || 'Untitled Project';
    const key = buildCartKey(projectTitle, component.id);
    setUniversalCart((prev) =>
      prev.map((item) =>
        item.key === key
          ? { ...item, selectedStoreId: storeId }
          : item
      )
    );
  };

  const clearUniversalCart = () => setUniversalCart([]);

  const handleSubmit = async (prompt) => {
    if (chatOpenTimerRef.current) {
      clearTimeout(chatOpenTimerRef.current);
      chatOpenTimerRef.current = null;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setActivePanel('none');
    setCurrentPrompt(prompt);
    try {
      const data = await getComponentList(prompt);
      setResult(data);
      chatOpenTimerRef.current = setTimeout(() => {
        setActivePanel('chat');
      }, 450);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleBomUpload = async (bomText, fileName) => {
    if (chatOpenTimerRef.current) {
      clearTimeout(chatOpenTimerRef.current);
      chatOpenTimerRef.current = null;
    }
    setLoading(true);
    setError(null);
    setResult(null);
    setActivePanel('none');
    setCurrentPrompt(`BOM:${fileName}`);
    try {
      const data = await getComponentListFromBom(bomText, fileName);
      setResult(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    if (chatOpenTimerRef.current) {
      clearTimeout(chatOpenTimerRef.current);
      chatOpenTimerRef.current = null;
    }
    setResult(null);
    setError(null);
    setActivePanel('none');
    setCurrentPrompt('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className={`app ${activePanel === 'chat' && result ? 'chat-active' : ''} ${activePanel === 'cart' ? 'cart-active' : ''}`}>
      <div className="gravity-particles-layer">
        <StarsBackground />
        <ShootingStars
          minSpeed={10}
          maxSpeed={30}
          minDelay={4200}
          maxDelay={8700}
          starColor="#9E00FF"
          trailColor="#2EB9DF"
          starWidth={10}
          starHeight={1}
        />
      </div>
      <Header
        cartCount={universalCart.length}
        theme={theme}
        onToggleTheme={toggleTheme}
        onOpenCart={() => {
          if (chatOpenTimerRef.current) {
            clearTimeout(chatOpenTimerRef.current);
            chatOpenTimerRef.current = null;
          }
          setActivePanel('cart');
        }}
      />
      <ImageMarquee />
      <main className="main-content">
        <HeroPrompt onSubmit={handleSubmit} loading={loading} />
        <BomUploader onUpload={handleBomUpload} loading={loading} />
        {error && (
          <div className="error-box animate-in">
            <AlertCircle size={18} />
            <div>
              <div className="error-title">Something went wrong</div>
              <div className="error-msg">{error}</div>
            </div>
            <button className="retry-btn" onClick={() => currentPrompt && handleSubmit(currentPrompt)}>
              <RefreshCw size={14} /> Retry
            </button>
          </div>
        )}
        {loading && <div className="results-section"><LoadingSkeleton /></div>}
        {result && !loading && (
          <div className="results-section animate-in">
            <div className="breadcrumb">
              <button className="bc-back" onClick={reset}>← New project</button>
              <span className="bc-sep">/</span>
              <span className="bc-current">{result.project?.title}</span>
            </div>
            <ProjectCard project={result.project} totalComponents={result.components?.length || 0} />
            <ComponentList
              project={result.project}
              components={result.components || []}
              tools={result.tools}
              tips={result.tips}
              warnings={result.warnings}
              cartKeySet={cartKeySet}
              onToggleCartItem={toggleComponentInCart}
              onAddManyToCart={addManyToCart}
              onRemoveManyFromCart={removeManyFromCart}
              cartItems={universalCart}
              onSelectStoreForCart={setCartItemStore}
            />
          </div>
        )}
        <ManufacturingShowcase />
        <StoreShowcase />
      </main>
      <MobileTaskbar
        cartCount={universalCart.length}
        theme={theme}
        onHome={() => {
          setActivePanel('none');
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }}
        onCategories={() => {
          setActivePanel('none');
          const section = document.getElementById('categories-anchor');
          if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
          window.dispatchEvent(new CustomEvent('open-mobile-categories'));
        }}
        onWishlist={() => {
          const section = document.getElementById('how');
          if (section) {
            section.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }}
        onCart={() => setActivePanel('cart')}
        onToggleTheme={toggleTheme}
      />
      <FooterBanner />
      <button
        className={`chat-fab ${activePanel === 'chat' ? 'active' : ''}`}
        onClick={() => {
          setActivePanel((prev) => (prev === 'chat' ? 'none' : 'chat'));
        }}
      >
        <MessageCircle size={22} />
        {activePanel !== 'chat' && <span className="fab-label">Ask Chippo</span>}
      </button>
      <ChatSidebar
        projectTitle={result?.project?.title}
        isOpen={activePanel === 'chat'}
        onClose={() => setActivePanel('none')}
      />
      <UniversalCart
        isOpen={activePanel === 'cart'}
        onClose={() => setActivePanel('none')}
        items={universalCart}
        onRemoveItem={removeCartItem}
        onUpdateItemQuantity={updateCartItemQuantity}
        onClearAll={clearUniversalCart}
      />
    </div>
  );
}
